import { GQL } from "@binale-tech/shared";

import { summarizeAutoSort } from "@dms/components/DmsAutoSort/autoSortSummary";
import type { MessageId } from "@domain/intl/useTranslation";
import { DmsType } from "@domain/models/dms";

import { withThinking } from "../runner/runAgent";
import type { AgentContext, AgentDetailGroup, AgentDetailItem, AgentStep, ReportLine } from "../runner/types";
import { MAX_IDS_PER_JOB } from "./draftSelection";
import { sendInChunks } from "./paging";

const Outcome = GQL.IDmsAutoSortOutcome;
const Reason = GQL.IDmsAutoSortReason;

// the folder of a moved result when the server did not send a target type
const fallbackType: Partial<Record<GQL.IDmsAutoSortOutcome, string>> = {
    [Outcome.MovedEr]: DmsType.ER,
    [Outcome.MovedDeb]: DmsType.Deb,
    [Outcome.MovedBank]: DmsType.Bank,
    [Outcome.MovedKb]: DmsType.KB,
};

const reasonTexts: Partial<Record<GQL.IDmsAutoSortReason, MessageId>> = {
    [Reason.BankAmbiguous]: "app.agent.details.autoSort.reason.bankAmbiguous",
    [Reason.NoBankMatch]: "app.agent.details.autoSort.reason.noBankMatch",
    [Reason.PaymentMixed]: "app.agent.details.autoSort.reason.paymentMixed",
    [Reason.PaymentUnknown]: "app.agent.details.autoSort.reason.paymentUnknown",
    [Reason.Informational]: "app.agent.details.autoSort.reason.informational",
    [Reason.NotProcessed]: "app.agent.details.autoSort.reason.notProcessed",
    [Reason.NotInNewDocuments]: "app.agent.details.autoSort.reason.notInNewDocuments",
    [Reason.Error]: "app.agent.details.autoSort.reason.error",
};

// an unchanged document needs the user (sort questions), except these
const unchangedGroups: Partial<Record<GQL.IDmsAutoSortReason, AgentDetailGroup>> = {
    [Reason.Error]: "refused",
    [Reason.NotInNewDocuments]: "untouched",
    [Reason.NotProcessed]: "untouched",
};

const movedLine = (ctx: AgentContext, result: GQL.IDmsAutoSortResult): ReportLine => {
    const type = result.targetType || fallbackType[result.outcome];
    if (!type) {
        return { textId: "app.agent.details.autoSort.movedUnknown" };
    }
    const folder = ctx.dmsFolder(type, result.targetSubType);
    const values = {
        ...(folder.labelId ? {} : { folder: folder.type }),
        ...(folder.subName ? { subFolder: folder.subName } : {}),
    };
    return {
        textId: folder.subName ? "app.agent.details.autoSort.movedSub" : "app.agent.details.autoSort.moved",
        ...(Object.keys(values).length > 0 ? { values } : {}),
        ...(folder.labelId ? { labels: { folder: folder.labelId } } : {}),
    };
};

const toDetail = (ctx: AgentContext, result: GQL.IDmsAutoSortResult): AgentDetailItem => {
    const ref = { kind: "document" as const, id: result.documentId };
    if (result.outcome !== Outcome.Unchanged) {
        return { group: "done", ref, outcome: movedLine(ctx, result) };
    }
    const reason = result.reason ?? undefined;
    return {
        group: (reason ? unchangedGroups[reason] : undefined) ?? "needsYou",
        ref,
        outcome: { textId: (reason ? reasonTexts[reason] : undefined) ?? "app.agent.details.autoSort.reason.unclear" },
    };
};

export const autoSortStep: AgentStep = {
    id: "autoSort",
    product: [GQL.IProductKey.Dms],
    async run(ctx, io) {
        const ids = [...ctx.documents().values()]
            .filter((doc) => doc.type === DmsType.new_documents && doc.aiStatus?.status === "done")
            .map((doc) => String(doc.key));
        if (ids.length === 0) return { status: "nothing" };

        const approved = await io.approve({
            textId: "app.agent.autoSort.approve.question",
            values: { count: ids.length },
            goLabelId: "app.agent.autoSort.approve.go",
            learnMoreId: "app.agent.autoSort.learnMore",
        });
        if (!approved) return { status: "skipped", byUser: true };

        const results = await withThinking(io, "app.agent.autoSort.thinking", () =>
            sendInChunks(ids, MAX_IDS_PER_JOB, (chunk) => ctx.autoSort(chunk)),
        );
        ctx.autoSortResults = results;
        const details = results.map((result) => toDetail(ctx, result));

        const { moved } = summarizeAutoSort(results);
        const values = { moved, total: results.length };
        if (moved === 0) {
            io.post({ kind: "nothing", textId: "app.agent.autoSort.done", values, details });
            return { status: "nothing", details };
        }
        io.post({ kind: "done", textId: "app.agent.autoSort.done", values, details });
        return {
            status: "done",
            summary: [{ textId: "app.agent.report.sorted", values: { moved, total: results.length } }],
            details,
        };
    },
};
